import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from 'react';
import '../../styles/InoImage.css';

interface ImageComponentProps {
  src: string;
  alt: string;
  fallbackSrc?: string;
  width?: number;
  height?: number;
  classNames?: string;
  loading?: boolean;
  customLoader?: ReactElement;
  lazy?: boolean;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  priority?: boolean;
  minDisplayTime?: number;
  useIntersectionObserver?: boolean;
}

type ImageState = 'idle' | 'loading' | 'loaded' | 'error';

const INTERSECTION_THRESHOLD = 0.1;
const INTERSECTION_ROOT_MARGIN = '50px';

export const InoImage: React.FC<ImageComponentProps> = memo(
  ({
    src,
    alt,
    fallbackSrc,
    width = 30,
    height = 30,
    loading = false,
    customLoader,
    classNames = '',
    lazy = true,
    objectFit = 'cover',
    priority = false,
    minDisplayTime,
    useIntersectionObserver = true,
  }) => {
    const [imageState, setImageState] = useState<ImageState>('idle');
    // Initialize currentSrc based on whether we should delay the request
    const shouldDelayRequest = minDisplayTime && minDisplayTime > 0;
    const initialIsInView = !lazy || priority || !useIntersectionObserver;
    const [currentSrc, setCurrentSrc] = useState<string>(
      initialIsInView && !shouldDelayRequest ? src : ''
    );
    const [isInView, setIsInView] = useState<boolean>(initialIsInView);

    const containerRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
    const minDisplayTimeRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      if (!lazy || priority || isInView || !useIntersectionObserver) return;
      if (!containerRef.current) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsInView(true);
              observer.disconnect();
            }
          });
        },
        {
          threshold: INTERSECTION_THRESHOLD,
          rootMargin: INTERSECTION_ROOT_MARGIN,
        }
      );

      observer.observe(containerRef.current);
      intersectionObserverRef.current = observer;

      return () => {
        if (intersectionObserverRef.current) {
          intersectionObserverRef.current.disconnect();
        }
      };
    }, [lazy, priority, isInView, useIntersectionObserver]);

    useEffect(() => {
      if (minDisplayTimeRef.current) {
        clearTimeout(minDisplayTimeRef.current);
        minDisplayTimeRef.current = null;
      }

      if (isInView) {
        setCurrentSrc('');
        setImageState('loading');

        // If minDisplayTime is provided, delay the image request
        if (minDisplayTime && minDisplayTime > 0) {
          minDisplayTimeRef.current = setTimeout(() => {
            setCurrentSrc(src);
            minDisplayTimeRef.current = null;
          }, minDisplayTime);
        } else {
          // No delay, set src immediately
          setCurrentSrc(src);
        }
      } else {
        // Not in view, clear currentSrc
        setCurrentSrc('');
        setImageState('idle');
      }
    }, [src, isInView, minDisplayTime]);

    useEffect(() => {
      return () => {
        if (minDisplayTimeRef.current) {
          clearTimeout(minDisplayTimeRef.current);
          minDisplayTimeRef.current = null;
        }
      };
    }, []);

    const handleImageLoad = useCallback(() => {
      if (minDisplayTimeRef.current) {
        clearTimeout(minDisplayTimeRef.current);
        minDisplayTimeRef.current = null;
      }

      setImageState('loaded');
    }, []);

    // Handle image error
    const handleImageError = useCallback(() => {
      if (fallbackSrc && fallbackSrc !== currentSrc) {
        setCurrentSrc(fallbackSrc);
        setImageState('loading');
      } else {
        setImageState('error');
      }
    }, [fallbackSrc, currentSrc]);

    const containerStyle = useMemo(
      () => ({
        width: `${width}px`,
        height: `${height}px`,
      }),
      [width, height]
    );

    const imageStyle = useMemo(
      () => ({
        objectFit,
        opacity: imageState === 'loaded' ? 1 : 0,
      }),
      [objectFit, imageState]
    );

    const imageClassName = useMemo(() => {
      const classes = ['ino-image__img'];
      if (imageState === 'loading') classes.push('loading');
      if (imageState === 'loaded') classes.push('loaded');
      return classes.join(' ');
    }, [imageState]);

    return (
      <div
        ref={containerRef}
        style={containerStyle}
        className={`ino-image ${classNames}`}
      >
        {imageState === 'loading' &&
          (loading ||
            customLoader ||
            (minDisplayTime && minDisplayTime > 0)) && (
            <div className="ino-image__placeholder">
              {customLoader || <p>loading...</p>}
            </div>
          )}

        {imageState === 'error' &&
          (fallbackSrc ? (
            <img
              src={fallbackSrc}
              alt={alt}
              width={width}
              height={height}
              style={{ objectFit }}
              className="ino-image__img loaded"
            />
          ) : (
            <div
              className="ino-image--error"
              role="alert"
              aria-label={`Failed to load image: ${alt}`}
            >
            </div>
          ))}

        {isInView && imageState !== 'error' && currentSrc && (
          <img
            ref={imgRef}
            src={currentSrc}
            alt={alt}
            width={width}
            height={height}
            style={imageStyle}
            className={imageClassName}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading={lazy && !priority ? 'lazy' : 'eager'}
            decoding="async"
          />
        )}
      </div>
    );
  }
);

InoImage.displayName = 'InoImage';
